<template>
  <div class="d-flex justify-space-between align-center">
    <div>
      <v-btn
        :disabled="leftDisabled"
        @click="navigateLeft()"
        color="primary"
        icon
      >
        <v-icon>{{ leftIcon }}</v-icon></v-btn
      >
    </div>
    <v-carousel
      :hide-delimiter-background="true"
      :hide-delimiters="true"
      :show-arrows="false"
      :height="128 + 16"
      v-model="carouselIndex"
      ref="carousel"
    >
      <v-carousel-item
        v-for="(slide, slideIndex) in slides"
        :key="slide.toString() + slideIndex"
      >
        <div class="d-flex justify-space-around">
          <div
            v-for="(medium, mediumIndex) in slide"
            :key="getMediumKey(medium, mediumIndex, slideIndex)"
          >
            <PostGalleryShowMore
              @reorder="reloadPostMedia"
              class="item-size"
              v-if="medium === 'more'"
              :total-elements="post.event_media_count"
              :post="dynamicPost"
            ></PostGalleryShowMore>
            <div class="item-size" v-else-if="medium === 'placeholder'"></div>
            <ServicedGalleryThumbnail
              v-else
              :image="medium.uploaded_file.thumb.url"
              style="cursor: pointer"
              @click="openImage(medium, slideIndex, mediumIndex)"
              :medium="medium"
            >
            </ServicedGalleryThumbnail>
          </div>
        </div>
      </v-carousel-item>
    </v-carousel>
    <div>
      <v-btn
        :disabled="rightDisabled"
        @click="navigateRight()"
        color="primary"
        icon
      >
        <v-icon>{{ rightIcon }}</v-icon></v-btn
      >
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  onMounted,
  onUnmounted,
  PropType,
  Ref,
  ref,
  watch,
  WritableComputedRef,
} from "vue"
import { Post } from "suus-api"
import { mdiArrowLeft, mdiArrowRight } from "@mdi/js"
import { GalleryMedium } from "features/gallery/gallery.types"
import { usePostGalleryCarousel } from "features/posts/gallery/carousel.concern"
import PostGalleryShowMore from "features/posts/gallery/PostGalleryShowMore.vue"
import { LightboxService } from "features/gallery/lightbox/lightbox.service"
import ServicedGalleryThumbnail from "features/gallery/service/ServicedGalleryThumbnail.vue"
import { PostLightboxService } from "features/posts/gallery/lightbox/post-lightbox.service"
import { eventMediaApi } from "store/api"

export type GallerySlide = (GalleryMedium | "placeholder" | "more")[]

export default defineComponent({
  name: "PostGalleryPreviewDesktop",
  components: {
    ServicedGalleryThumbnail,
    PostGalleryShowMore,
  },
  props: {
    post: {
      type: Object as PropType<Post>,
      required: true,
    },
    canEditHighlights: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const dynamicPost: Ref<Post> = ref(props.post)
    watch(
      () => props.post,
      (post) => {
        dynamicPost.value = post
      }
    )

    const reloadPostMedia = async () => {
      const response = await eventMediaApi.getEventMedia(
        props.post.id.toString(),
        0,
        8,
        "asc"
      )
      dynamicPost.value = {
        ...dynamicPost.value,
        event_media_preview: response.data,
      }
      console.log(dynamicPost.value)
      console.log("calling resize")
      onResize()
    }

    const _carouselIndex: Ref<number> = ref(0)

    const carouselIndex: WritableComputedRef<number> = computed({
      get: () => _carouselIndex.value,
      set: (value) => {
        _carouselIndex.value = value
      },
    })

    const leftIcon = mdiArrowLeft
    const rightIcon = mdiArrowRight

    const carousel = ref(null)

    const { slides, resizeSlides, slideItemsCount } = usePostGalleryCarousel()

    const onResize = () => {
      resizeSlides(carousel.value.$el, dynamicPost.value)
    }

    onMounted(() => {
      onResize()
      window.addEventListener("resize", onResize)
    })

    onUnmounted(() => {
      window.removeEventListener("resize", onResize)
    })

    const navigateLeft = () => {
      if (carouselIndex.value > 0) {
        carouselIndex.value -= 1
      }
    }

    const navigateRight = () => {
      if (carouselIndex.value < slides.value.length - 1) {
        carouselIndex.value += 1
      }
    }

    const leftDisabled = computed(() => carouselIndex.value === 0)
    const rightDisabled = computed(
      () => carouselIndex.value === slides.value.length - 1
    )

    const lightboxService: LightboxService = inject("lightboxService")
    const postLightboxService: PostLightboxService | null = inject(
      "PostLightboxService",
      null
    )
    const openImage = (
      medium: GalleryMedium,
      slideIndex: number,
      mediumIndex: number
    ) => {
      if (postLightboxService) {
        postLightboxService.openLightbox(
          slideIndex * slideItemsCount.value + mediumIndex
        )
        return
      }
      lightboxService.openLightbox(medium.uploaded_file.original.url)
    }

    const getMediumKey = (
      medium: GalleryMedium | "placeholder" | "more",
      index: number,
      slideIndex: number
    ) => {
      if (medium === "placeholder" || medium === "more") {
        return medium + "-" + index + "-" + slideIndex
      } else {
        return medium.id.toString() + "-" + index + "-" + slideIndex
      }
    }

    return {
      carouselIndex,
      leftIcon,
      rightIcon,
      slides,
      carousel,
      navigateLeft,
      navigateRight,
      leftDisabled,
      rightDisabled,
      openImage,
      reloadPostMedia,
      dynamicPost,
      getMediumKey,
    }
  },
})
</script>

<style>
.item-size {
  height: 128px;
  width: 128px;
}
</style>
